import { devWarn, devError, devInfo } from "@/lib/logger";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { createInvoice, listInvoices, voidInvoice } from "@/lib/api";

// Monotonic client-side id for line items — used as stable React key so mid-list
// deletes don't collapse two rows into the same key.
let _lineSeq = 0;
const nextLineId = () => `line_${++_lineSeq}`;

const empty = () => ({ _lid: nextLineId(), description: "", quantity: 1, unit_amount: "" });

const STATUS_STYLES = {
  paid:  "text-[#22c55e] border-[#22c55e]/50",
  open:  "text-[#60a5fa] border-[#60a5fa]/50",
  draft: "text-zinc-200 border-zinc-700",
  uncollectible: "text-[#fb7185] border-[#fb7185]/50",
  void:  "text-zinc-300 border-zinc-800",
  past_due: "text-[#ffc439] border-[#ffc439]/50",
};

export default function InvoicesWidget() {
  const [invoices, setInvoices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    client_name: "",
    client_email: "",
    client_line1: "",
    client_city: "",
    client_state: "NC",
    client_postal: "",
    client_country: "US",
    days_until_due: 14,
    auto_tax: true,
    memo: "",
    lines: [empty()],
  });

  const load = useCallback(async () => {
    try {
      const r = await listInvoices();
      setInvoices(r.items || []);
    } catch (err) {
      devWarn("invoices load failed:", err?.message || err);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setLine = (i, patch) => {
    const next = form.lines.slice();
    next[i] = { ...next[i], ...patch };
    setForm({ ...form, lines: next });
  };
  const addLine = () => setForm({ ...form, lines: [...form.lines, empty()] });
  const rmLine = (i) => setForm({ ...form, lines: form.lines.filter((_, j) => j !== i) });

  const total = form.lines.reduce(
    (s, l) => s + (parseFloat(l.unit_amount || 0) * parseFloat(l.quantity || 0)),
    0,
  );

  const submit = async () => {
    if (!form.client_name.trim() || !form.client_email.includes("@")) {
      toast.error("Client name + valid email required"); return;
    }
    const cleanLines = form.lines
      .map((l) => ({
        description: l.description.trim(),
        quantity: parseFloat(l.quantity),
        unit_amount: parseFloat(l.unit_amount),
      }))
      .filter((l) => l.description && l.quantity > 0 && l.unit_amount > 0);
    if (cleanLines.length === 0) { toast.error("Add at least one valid line item"); return; }

    setBusy(true);
    try {
      const inv = await createInvoice({ ...form, lines: cleanLines });
      toast.success(`Invoice ${inv.number || ""} sent to ${form.client_email}`);
      setForm({
        client_name: "", client_email: "", client_line1: "", client_city: "",
        client_state: "NC", client_postal: "", client_country: "US",
        days_until_due: 14, auto_tax: true, memo: "", lines: [empty()],
      });
      setCreating(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invoice failed");
    } finally { setBusy(false); }
  };

  const doVoid = async (iid) => {
    if (!window.confirm("Void this invoice? Cannot be undone.")) return;
    try {
      await voidInvoice(iid);
      toast.success("Invoice voided");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Void failed");
    }
  };

  return (
    <div data-testid="invoices-widget" className="border border-zinc-800 bg-zinc-950/60 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#ffc439]">// nc construction · invoices</div>
          <div className="text-[10px] font-mono text-zinc-300 mt-0.5">Stripe Invoicing + auto-tax</div>
        </div>
        <button
          data-testid="new-invoice-toggle"
          onClick={() => setCreating(!creating)}
          className="text-[10px] tracking-[0.3em] uppercase text-black bg-[#ffc439] hover:bg-[#ffb800] px-3 py-2 font-bold transition-colors"
        >{creating ? "Close" : "+ New invoice"}</button>
      </div>

      {creating && (
        <div className="border border-[#ffc439]/40 bg-[#ffc439]/5 p-4 mb-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input data-testid="inv-client-name" placeholder="Client name" value={form.client_name}
              onChange={(e) => setForm({ ...form, client_name: e.target.value })}
              className="bg-black border border-zinc-800 focus:border-[#ffc439] focus:outline-none text-white placeholder-zinc-300 font-mono text-xs px-3 py-2" />
            <input data-testid="inv-client-email" placeholder="Client email" type="email" value={form.client_email}
              onChange={(e) => setForm({ ...form, client_email: e.target.value })}
              className="bg-black border border-zinc-800 focus:border-[#ffc439] focus:outline-none text-white placeholder-zinc-300 font-mono text-xs px-3 py-2" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <input placeholder="Address" value={form.client_line1}
              onChange={(e) => setForm({ ...form, client_line1: e.target.value })}
              className="sm:col-span-2 bg-black border border-zinc-800 focus:border-[#ffc439] focus:outline-none text-white placeholder-zinc-300 font-mono text-xs px-3 py-2" />
            <input placeholder="City" value={form.client_city}
              onChange={(e) => setForm({ ...form, client_city: e.target.value })}
              className="bg-black border border-zinc-800 focus:border-[#ffc439] focus:outline-none text-white placeholder-zinc-300 font-mono text-xs px-3 py-2" />
            <div className="flex gap-2">
              <input placeholder="ST" maxLength={2} value={form.client_state}
                onChange={(e) => setForm({ ...form, client_state: e.target.value.toUpperCase().slice(0,2) })}
                className="w-14 bg-black border border-zinc-800 focus:border-[#ffc439] focus:outline-none text-white placeholder-zinc-300 font-mono text-xs px-3 py-2" />
              <input placeholder="ZIP" value={form.client_postal}
                onChange={(e) => setForm({ ...form, client_postal: e.target.value })}
                className="flex-1 bg-black border border-zinc-800 focus:border-[#ffc439] focus:outline-none text-white placeholder-zinc-300 font-mono text-xs px-3 py-2" />
            </div>
          </div>

          <div className="border-t border-zinc-800 pt-2">
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-200 mb-2">Line items</div>
            {form.lines.map((l, i) => (
              <div key={l._lid} className="grid grid-cols-12 gap-2 mb-2">
                <input placeholder="Description (labor / materials / job)" value={l.description}
                  onChange={(e) => setLine(i, { description: e.target.value })}
                  className="col-span-6 bg-black border border-zinc-800 focus:border-[#ffc439] focus:outline-none text-white placeholder-zinc-300 font-mono text-xs px-3 py-2" />
                <input placeholder="Qty" type="number" min="0" step="0.5" value={l.quantity}
                  onChange={(e) => setLine(i, { quantity: e.target.value })}
                  className="col-span-2 bg-black border border-zinc-800 focus:border-[#ffc439] focus:outline-none text-white placeholder-zinc-300 font-mono text-xs px-3 py-2" />
                <input placeholder="Unit $" type="number" min="0" step="0.01" value={l.unit_amount}
                  onChange={(e) => setLine(i, { unit_amount: e.target.value })}
                  className="col-span-3 bg-black border border-zinc-800 focus:border-[#ffc439] focus:outline-none text-white placeholder-zinc-300 font-mono text-xs px-3 py-2" />
                <button onClick={() => rmLine(i)} disabled={form.lines.length === 1}
                  className="col-span-1 text-zinc-300 hover:text-[#fb7185] font-mono text-xs disabled:opacity-30">×</button>
              </div>
            ))}
            <button onClick={addLine} className="text-[10px] tracking-[0.3em] uppercase text-[#ffc439] hover:bg-[#ffc439]/10 border border-[#ffc439]/40 px-3 py-1.5">+ Line</button>
          </div>

          <div className="flex flex-wrap gap-3 items-center pt-2">
            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-200 flex items-center gap-2">
              <input type="checkbox" checked={form.auto_tax}
                onChange={(e) => setForm({ ...form, auto_tax: e.target.checked })}
                className="accent-[#ffc439]" />
              Auto-tax
            </label>
            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-200 flex items-center gap-2">
              Due in
              <input type="number" min="0" max="180" value={form.days_until_due}
                onChange={(e) => setForm({ ...form, days_until_due: parseInt(e.target.value || 14) })}
                className="w-16 bg-black border border-zinc-800 focus:outline-none text-white font-mono text-xs px-2 py-1" />
              days
            </label>
            <div className="ml-auto font-mono text-sm text-white">Subtotal <span className="text-[#ffc439]">${total.toFixed(2)}</span></div>
          </div>

          <input placeholder="Memo (optional — appears on invoice)" value={form.memo}
            onChange={(e) => setForm({ ...form, memo: e.target.value })}
            className="w-full bg-black border border-zinc-800 focus:border-[#ffc439] focus:outline-none text-white placeholder-zinc-300 font-mono text-xs px-3 py-2" />

          <button
            data-testid="inv-send-button"
            onClick={submit}
            disabled={busy}
            className="w-full text-[11px] tracking-[0.3em] uppercase text-black bg-[#ffc439] hover:bg-[#ffb800] px-4 py-3 font-bold transition-colors disabled:opacity-50"
          >{busy ? "Sending…" : "Send invoice →"}</button>
        </div>
      )}

      <div className="max-h-72 overflow-y-auto scrollbar-thin pr-1">
        {invoices.length === 0 && !creating && (
          <div className="text-xs text-zinc-300 font-mono py-3">
            // no invoices yet · Stripe Invoicing + Tax ready. Click &quot;+ New invoice&quot; to send your first.
          </div>
        )}
        {invoices.map((inv) => {
          const st = String(inv.status || "draft").toLowerCase();
          const styles = STATUS_STYLES[st] || STATUS_STYLES.draft;
          return (
            <div key={inv.id} className="flex items-center justify-between gap-2 border-b border-zinc-900 py-3 text-xs">
              <div className="min-w-0 flex-1">
                <div className="font-mono text-white truncate">{inv.client_name} <span className="text-zinc-300">· {inv.client_email}</span></div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-300 mt-0.5">
                  {inv.number || inv.id.slice(0, 10)} · {new Date(inv.created_at).toLocaleDateString()}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono text-white">${((inv.amount_due || 0) / 100).toFixed(2)}</div>
                <span className={`inline-block text-[9px] tracking-[0.25em] uppercase border px-1.5 py-0.5 mt-1 ${styles}`}>{st}</span>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                {inv.hosted_invoice_url && (
                  <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
                    className="text-[9px] tracking-[0.25em] uppercase text-[#ffc439] hover:underline">Open →</a>
                )}
                {st === "open" && (
                  <button onClick={() => doVoid(inv.id)} className="text-[9px] tracking-[0.25em] uppercase text-zinc-400 hover:text-[#fb7185]">Void</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
